import React from "react";
import { Link } from "react-router-dom";
import {
  Music2,
  Palette,
  Dices,
  Calculator,
  Navigation,
  Compass,
  ChevronRight,
} from "lucide-react";
import Layout from "../components/Layout";
import { useLanguage } from "../i18n/LanguageContext";

/**
 * Walkthrough / tutorial page.
 * Top: video player with placeholder (file can be added later to /tutorial.mp4)
 * Below: per-module knowledge-base cards.
 */
const FEATURES = [
  {
    to: "/metronome",
    key: "metronome",
    Icon: Music2,
    accent: "cyan",
    stepsPl: [
      "Wybierz tempo BPM: 100 / 120 / 140 / 160",
      "Kliknij START — usłyszysz dźwięk metronomu",
      "Włącz 'Sterowanie głosem' i powiedz 'STOP' aby zakończyć",
    ],
    stepsEn: [
      "Pick BPM tempo: 100 / 120 / 140 / 160",
      "Tap START — you'll hear the metronome",
      "Enable 'Voice control' and say 'STOP' to finish",
    ],
  },
  {
    to: "/colors",
    key: "colors",
    Icon: Palette,
    accent: "orange",
    stepsPl: [
      "Ustaw szybkość wyświetlania suwakiem",
      "Kliknij START — pojawi się losowy kolor",
      "Mów na głos widziany kolor (np. 'żółty')",
      "Powiedz 'STOP' — zobaczysz wynik (poprawne / wszystkie)",
    ],
    stepsEn: [
      "Set display speed with the slider",
      "Tap START — a random color appears",
      "Say the color out loud (e.g. 'yellow')",
      "Say 'STOP' — see your score (correct / total)",
    ],
  },
  {
    to: "/colors-numbers",
    key: "colorsNumbers",
    Icon: Dices,
    accent: "cyan",
    stepsPl: [
      "Ustaw szybkość wyświetlania",
      "Kliknij START — pojawi się kolor + cyfra 1-10",
      "Wykonuj ćwiczenie reagując na bodźce wzrokowe",
    ],
    stepsEn: [
      "Set display speed",
      "Tap START — color + digit 1-10 appears",
      "Train by reacting to both stimuli at once",
    ],
  },
  {
    to: "/math",
    key: "math",
    Icon: Calculator,
    accent: "orange",
    stepsPl: [
      "Ustaw szybkość wyświetlania",
      "Kliknij START — pojawi się działanie np. '5+5='",
      "Mów wynik głosowo (np. 'dziesięć' lub '10')",
      "Powiedz 'STOP' — zobaczysz wynik sesji",
    ],
    stepsEn: [
      "Set display speed",
      "Tap START — equation like '5+5=' appears",
      "Say the answer out loud ('ten' or '10')",
      "Say 'STOP' — see session result",
    ],
  },
  {
    to: "/arrows",
    key: "arrows",
    Icon: Navigation,
    accent: "cyan",
    stepsPl: [
      "Ustaw szybkość wyświetlania",
      "Kliknij START — pojawi się czarna strzałka (4 kierunki)",
      "Reaguj odpowiednim ruchem ciała / piłki",
    ],
    stepsEn: [
      "Set display speed",
      "Tap START — black arrow appears (4 directions)",
      "React with proper movement of body / ball",
    ],
  },
  {
    to: "/arrows-diag",
    key: "arrowsDiag",
    Icon: Compass,
    accent: "orange",
    stepsPl: [
      "Ustaw szybkość wyświetlania",
      "Kliknij START — pojawi się strzałka w jednym z 8 kierunków",
      "Reaguj ruchem także w skos",
    ],
    stepsEn: [
      "Set display speed",
      "Tap START — arrow in one of 8 directions appears",
      "React with diagonal movements too",
    ],
  },
];

export default function Tutorial() {
  const { t, lang } = useLanguage();
  const useEn = lang === "en" || lang === "de" || lang === "es" || lang === "fr" || lang === "uk";
  const [videoError, setVideoError] = React.useState(false);

  return (
    <Layout showBack>
      <div className="vls-page vls-tutorial" data-testid="tutorial-page">
        <h1 className="vls-page-title">
          <span className="vls-grad-cyan">{t("tutorial")}</span>
        </h1>

        {/* === Video module === */}
        <section className="vls-tut-video" data-testid="tut-video-section">
          <h2 className="vls-tut-section-title">{t("tutorialVideoTitle")}</h2>
          {!videoError ? (
            <div className="vls-tut-video-frame">
              <video
                controls
                preload="metadata"
                playsInline
                poster="/logo.png"
                className="vls-tut-video-el"
                data-testid="tut-video-player"
                onError={(e) => {
                  const v = e.currentTarget;
                  if (v && v.networkState === 3 /* NETWORK_NO_SOURCE */) {
                    setVideoError(true);
                  }
                }}
              >
                <source src="/tutorial.mp4" type="video/mp4" />
                {t("tutorialVideoSoon")}
              </video>
            </div>
          ) : (
            <div
              className="vls-tut-video-frame vls-tut-video-placeholder"
              data-testid="tut-video-placeholder"
            >
              <p>{t("tutorialVideoSoon")}</p>
            </div>
          )}
          <p className="vls-tut-video-note">{t("tutorialVideoSoon")}</p>
        </section>

        {/* === Feature cards === */}
        <section className="vls-tut-features">
          <h2 className="vls-tut-section-title">{t("tutorialFeaturesTitle")}</h2>
          <div className="vls-tut-grid">
            {FEATURES.map(({ to, key, Icon, accent, stepsPl, stepsEn }) => {
              const steps = useEn ? stepsEn : stepsPl;
              return (
                <article
                  key={key}
                  className={`vls-tut-card vls-accent-${accent}`}
                  data-testid={`tut-card-${key}`}
                >
                  <div className="vls-tut-card-visual">
                    <Icon size={64} strokeWidth={1.6} />
                  </div>
                  <div className="vls-tut-card-body">
                    <h3 className="vls-tut-card-title">{t(key)}</h3>
                    <p className="vls-tut-card-desc">{t(`${key}Desc`)}</p>
                    <p className="vls-tut-card-steps-label">
                      {t("tutorialSteps")}
                    </p>
                    <ol className="vls-tut-card-steps">
                      {steps.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ol>
                    <Link
                      to={to}
                      className="vls-tut-card-cta"
                      data-testid={`tut-open-${key}`}
                    >
                      <span>{t("tutorialOpenModule")}</span>
                      <ChevronRight size={16} />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </Layout>
  );
}
